// Bảng tổng kết của `avatar sync --all` (#211 phase-02).
//
// KHÔNG phải trang trí — 3 trong 4 vế dưới đây là thứ đổi lấy các quyết định đã chốt:
//
//  1. `current_version` in THÔ (`local-2026-08-20`, `v1.1.67`, `unknown`) — lá chắn DUY NHẤT
//     thay cho luật `skipped:local-install` đã chốt KHÔNG thêm (Luke, 2026-08-20): người
//     dogfood pack build tay phải NHÌN THẤY nơi nào đang chạy local TRƯỚC khi nó bị ghi đè.
//  2. `skipped:open-session` in ĐÍCH DANH + gợi ý cờ kèm PATH CỦA CHÍNH workspace đó. Cửa sổ
//     30 phút rộng có chủ đích, và chỉ chấp nhận được vì có dòng này: sai dương ở CLI = bỏ
//     qua im lặng, dòng in ra là thứ biến nó thành hiển nhiên. In cờ dạng TRẦN = dạy người
//     dùng override toàn máy.
//  3. `skipped:stale` in kèm LỐI THOÁT. Không có nó thì stale là VÒNG KHOÁ: stale ⇒ không
//     spawn con ⇒ `lastSyncAt`/`lastSeenAt` đứng yên ⇒ lần sau vẫn stale, vĩnh viễn.
//  4. Nhắc #195 (`sync` ghi đè `CLAUDE.md` sửa tay) — nay nhân theo N workspace.

import type { SkipReason } from "../lib/plan-sync-all-across-registered-workspaces.js";
import { log } from "../lib/terminal-logger.js";

export type RowOutcome = "ok" | "failed" | "skipped" | "pruned" | "will-sync" | "not-reached";

export interface SummaryRow {
  path: string;
  outcome: RowOutcome;
  /** Version THÔ đang cài trước khi chạy — `null` khi không đọc nổi. */
  currentVersion: string | null;
  reason?: SkipReason;
  detail?: string;
}

/** `null` ⇒ `unknown`: in thô, không bịa, không rút gọn. */
function showVersion(v: string | null): string {
  return v ?? "unknown";
}

function outcomeLabel(row: SummaryRow, targetVersion: string): string {
  switch (row.outcome) {
    case "ok":
      return `ok         ${showVersion(row.currentVersion)} → ${targetVersion}`;
    case "failed":
      return `FAILED     ${showVersion(row.currentVersion)} (giữ nguyên)`;
    case "pruned":
      return "pruned     (workspace không còn trên đĩa)";
    case "will-sync":
      return `sẽ sync    ${showVersion(row.currentVersion)} → ${targetVersion}`;
    case "not-reached":
      return `chưa chạy  ${showVersion(row.currentVersion)} (bị ngắt trước khi tới)`;
    case "skipped":
      return `skipped:${row.reason ?? "?"} ${showVersion(row.currentVersion)}`;
  }
}

export function renderSyncAllSummaryTable(
  rows: readonly SummaryRow[],
  targetVersion: string,
): void {
  log.info(`\nTổng kết sync --all (đích: ${targetVersion})`);
  if (rows.length === 0) {
    log.plain("  (registry rỗng — chưa workspace nào được đăng ký)");
    return;
  }

  for (const row of rows) {
    log.plain(`  ${row.path}`);
    log.plain(`      ${outcomeLabel(row, targetVersion)}${row.detail ? ` — ${row.detail}` : ""}`);
  }

  const counts = {
    ok: rows.filter((r) => r.outcome === "ok").length,
    failed: rows.filter((r) => r.outcome === "failed").length,
    skipped: rows.filter((r) => r.outcome === "skipped").length,
    pruned: rows.filter((r) => r.outcome === "pruned").length,
  };
  log.info(
    `${counts.ok} ok · ${counts.failed} failed · ${counts.skipped} skipped · ${counts.pruned} pruned`,
  );

  // Lối thoát in ĐÍCH DANH từng workspace — xem ghi chú (2) và (3) đầu file.
  for (const row of rows.filter((r) => r.outcome === "skipped" && r.reason === "open-session")) {
    log.warn(
      `Bỏ qua vì có phiên Claude Code sống: ${row.path}\n` +
        `  Ép sync đúng workspace này: avatar sync --all --force-open-sessions ${row.path}`,
    );
  }
  if (rows.some((r) => r.outcome === "skipped" && r.reason === "stale")) {
    log.warn(
      "Bỏ qua workspace quá hạn hoạt động. Vẫn muốn sync: avatar sync --all --stale-days 9999",
    );
  }

  if (rows.some((r) => r.outcome === "ok")) {
    log.dim(
      "  (#195: sync ghi đè phần managed trong CLAUDE.md — sửa tay trong block đó sẽ mất, nay ở nhiều workspace)",
    );
  }
}
